import { retainedSentimentSourceIds } from '../../../../../lib/reportGeneration/reportEvidenceSourceAdmission';
/**
 * Chapter 01 blocks (SPEC §3.01, §4): the hero (local support index with a
 * re-stopped CVD-safe meter), the template hero sentence, one square per local
 * record, the KPI rail and the coverage disclosure — or, when no local stance
 * exists, the status block in place of the hero. Coverage-only and
 * no-dated-local-stance reports NEVER get an index, band, meter or confidence.
 */
import React from 'react';
import type { ReportTheme } from '../../../../../types/reportTheme';
import { roleColor } from '../../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../../lib/reportSectionHeading';
import { EditorialKpiRail, type EditorialKpiItem } from '../../../report/editorial';
import type { PsrSourceCoverageState, PsrCoverageState } from '../../../../../lib/sentiment/publicSentimentMetaV2Contract';
import {
  PSR_BAND_LABELS,
  PSR_DEFAULT_NOT_ESTABLISHED_REASON,
  PSR_DISCLOSURE_FOOTNOTE,
  PSR_EMPTY_IS_NOT_NEUTRAL,
  PSR_NOT_A_SURVEY,
  PSR_STATUS_NOT_ESTABLISHED,
  PSR_STATUS_UNKNOWN,
  PSR_STATUS_UNKNOWN_LINE,
  psrBand,
  psrCoverageTally,
  psrCoverageTallyText,
  psrHeroSentence,
  psrKpiItems,
  psrLocalReasonChips,
  psrSourceLabel,
  psrStancedCount,
  type PsrReportView,
} from '../../../../../lib/sentiment/psrReportView';
import {
  CoverageMark,
  PSR_APPARATUS,
  PSR_CHART_WIDTH,
  PSR_SERIF,
  PsrCaveat,
  PsrGroupHeading,
  PsrPartialTag,
  PsrQuestionIcon,
  ReasonCountChip,
  psrPalette,
} from './psrUi';

/* ── Hero: assessed ────────────────────────────────────────────────────── */

/** The 0–100 meter, re-stopped opposed → neutral → supportive (SPEC deviation 1). */
export function SentimentIndexMeter({ theme, index }: { theme: ReportTheme; index: number }): React.ReactElement {
  const p = psrPalette(theme);
  const W = PSR_CHART_WIDTH;
  const x = (v: number) => (Math.max(0, Math.min(100, v)) / 100) * W;
  const gradientId = React.useId().replace(/:/g, '');
  const muted = roleColor(theme, 'muted');
  const faint = roleColor(theme, 'faint');
  const text = { fontFamily: PSR_APPARATUS, fontSize: 11, fontWeight: 500 } as const;
  return (
    <svg
      data-testid="psr-index-meter"
      viewBox={`0 0 ${W} 44`}
      width="100%"
      role="img"
      aria-label={`Local support index meter, marker at ${index} of 100`}
      style={{ display: 'block', maxWidth: '100%', overflow: 'visible' }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={p.opposed} />
          <stop offset="0.5" stopColor={p.neutral} />
          <stop offset="1" stopColor={p.supportive} />
        </linearGradient>
      </defs>
      <rect x="0" y="10" width={W} height="8" rx="4" fill={`url(#${gradientId})`} />
      {[35, 65].map(t => (
        <React.Fragment key={t}>
          <rect x={x(t) - 1.2} y="9" width="2" height="10" fill={p.surface} />
          <text x={x(t) + 0.8} y="36" textAnchor="middle" fill={muted} style={{ ...text, fontVariantNumeric: 'tabular-nums' }}>{t}</text>
        </React.Fragment>
      ))}
      <text x="0" y="36" textAnchor="start" fill={faint} style={text}>0</text>
      <text x={W} y="36" textAnchor="end" fill={faint} style={text}>100</text>
      <text x={x(17.5)} y="36" textAnchor="middle" fill={muted} style={text}>Opposed ≤35</text>
      <text x={x(50)} y="36" textAnchor="middle" fill={muted} style={text}>Mixed 36–64</text>
      <text x={x(82.5)} y="36" textAnchor="middle" fill={muted} style={text}>Supportive ≥65</text>
      <rect x={x(index) - 3} y="2" width="6" height="24" fill={p.surface} />
      <rect x={x(index) - 1} y="4" width="2" height="20" fill={roleColor(theme, 'ink')} />
    </svg>
  );
}

function eyebrow(theme: ReportTheme): React.CSSProperties {
  return { fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: roleColor(theme, 'muted'), lineHeight: 1.35 };
}

/** "53 /100 · Mixed band 36–64" + meter. Renders only with a real index. */
export function SentimentHero({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  if (view.index === undefined || !view.site || psrStancedCount(view.site) === 0) return null;
  const index = Math.round(view.index);
  const band = PSR_BAND_LABELS[psrBand(index)];
  const ink = roleColor(theme, 'ink');
  return (
    <div data-testid="psr-hero" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={eyebrow(theme)}>Local support index · dated local statements</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span data-testid="psr-hero-index" style={{ fontFamily: PSR_APPARATUS, fontSize: 48, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1, color: ink }}>{index}</span>
        <span style={{ fontFamily: PSR_APPARATUS, fontSize: 20, fontWeight: 500, color: roleColor(theme, 'muted') }}>/100</span>
        <span data-testid="psr-hero-band" style={{ fontFamily: PSR_APPARATUS, fontSize: 20, fontWeight: 600, color: ink }}>· {band.word}</span>
        <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, color: roleColor(theme, 'faint') }}>{band.range}</span>
        <span style={{ flex: 1 }} />
        {view.partial && <span style={{ alignSelf: 'center' }}><PsrPartialTag theme={theme} label="Partial coverage" /></span>}
      </div>
      <SentimentIndexMeter theme={theme} index={index} />
    </div>
  );
}

/** The template hero sentence (SSOT: `psrHeroSentence`). */
export function SentimentHeroSentence({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  if (view.index === undefined || !view.site) return null;
  return (
    <p data-testid="psr-hero-sentence" style={{ margin: 0, fontFamily: PSR_SERIF, fontSize: 15, lineHeight: 1.55, color: roleColor(theme, 'ink') }}>
      {psrHeroSentence(view.site)} <span style={{ color: roleColor(theme, 'muted') }}>{PSR_NOT_A_SURVEY}</span>
    </p>
  );
}

/** Drawn squares per group are capped; the printed count is always exact. */
const UNIT_STRIP_MAX_SQUARES = 200;

function UnitGroup({ theme, color, count, label, testId }: { theme: ReportTheme; color: string; count: number; label: string; testId: string }): React.ReactElement | null {
  if (count <= 0) return null;
  const drawn = Math.min(count, UNIT_STRIP_MAX_SQUARES);
  return (
    <div data-testid={testId} data-count={count} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }} aria-hidden="true">
        {Array.from({ length: drawn }, (_, i) => <span key={i} style={{ width: 14, height: 14, borderRadius: 2, background: color, display: 'block' }} />)}
      </div>
      <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, fontWeight: 600, color: roleColor(theme, 'body') }}>
        {label}{drawn < count ? ` (${drawn} drawn)` : ''}
      </span>
    </div>
  );
}

/** One square per local record: opposed · neutral · supportive | unscored. */
export function LocalRecordUnitStrip({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  if (view.index === undefined || !view.site) return null;
  const p = psrPalette(theme);
  const site = view.site;
  return (
    <div data-testid="psr-unit-strip" role="img" aria-label={psrHeroSentence(site)} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <UnitGroup theme={theme} color={p.opposed} count={site.opposed ?? 0} label={`${site.opposed ?? 0} opposed`} testId="psr-units-opposed" />
        <UnitGroup theme={theme} color={p.neutral} count={site.neutralStated ?? 0} label={`${site.neutralStated ?? 0} neutral`} testId="psr-units-neutral" />
        <UnitGroup theme={theme} color={p.supportive} count={site.supportive ?? 0} label={`${site.supportive ?? 0} supportive`} testId="psr-units-supportive" />
      </div>
      {site.unscored > 0 && (
        <>
          <span aria-hidden="true" style={{ width: 1, height: 30, background: roleColor(theme, 'rule'), display: 'block', flex: 'none' }} />
          <UnitGroup theme={theme} color={p.unscored} count={site.unscored} label={`${site.unscored} unscored, not in the index`} testId="psr-units-unscored" />
        </>
      )}
    </div>
  );
}

/* ── Status block: no local stance ─────────────────────────────────────── */

/**
 * In place of the hero when local stance is not established (B1) or unknown
 * (B2). Carries no number, band, meter or confidence.
 */
export function SentimentStatusBlock({ theme, view, reason }: { theme: ReportTheme; view: PsrReportView; reason?: string }): React.ReactElement | null {
  // Only the two no-stance products get a status. An assessed report whose
  // index did not survive (malformed persistence) shows neither a hero nor a
  // "not established" claim: the charts stay absent and the prose stands.
  if (view.index !== undefined || view.state === 'assessed') return null;
  const unknown = view.state === 'coverage-only';
  const chips = unknown ? [] : psrLocalReasonChips(view.v2);
  const localTotal = view.site?.total ?? 0;
  const line = unknown ? PSR_STATUS_UNKNOWN_LINE : (reason?.trim() || PSR_DEFAULT_NOT_ESTABLISHED_REASON);
  return (
    <div data-testid="psr-status-block" data-psr-status={unknown ? 'unknown' : 'not-established'} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={eyebrow(theme)}>{unknown ? 'Community stance and confidence' : 'Local stance · dated local statements'}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: roleColor(theme, 'ink') }}>
        <PsrQuestionIcon />
        <div data-testid="psr-status-title" style={{ fontFamily: 'var(--heading-font, var(--report-body-font, inherit))', fontSize: 26, fontWeight: 600, lineHeight: 1.15, letterSpacing: '-0.01em', color: roleColor(theme, 'ink') }}>
          {unknown ? PSR_STATUS_UNKNOWN : PSR_STATUS_NOT_ESTABLISHED}
        </div>
      </div>
      <p style={{ margin: 0, fontFamily: PSR_SERIF, fontSize: 15, lineHeight: 1.55, color: roleColor(theme, 'ink') }}>{line}</p>
      {chips.length > 0 && (
        <div data-testid="psr-status-reasons" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, fontWeight: 700, color: roleColor(theme, 'body') }}>
            {localTotal} local {localTotal === 1 ? 'record' : 'records'}, all unscored:
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {chips.map(chip => <ReasonCountChip key={chip.reasonCode} theme={theme} reasonCode={chip.reasonCode} count={chip.count} />)}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── KPI rail ──────────────────────────────────────────────────────────── */

/** SPEC §3.01 / §4 KPI rail — no index, band or confidence without local stance (`psrKpiItems`). */
export function SentimentKpiRail({ theme, view, entryCount }: { theme: ReportTheme; view: PsrReportView; entryCount?: number }): React.ReactElement | null {
  const items: EditorialKpiItem[] = psrKpiItems(view, entryCount).map(item => ({ key: item.key, value: item.value, ...(item.unit ? { unit: item.unit } : {}), ...(item.note ? { note: item.note } : {}) }));
  if (!items.length) return null;
  return <div data-testid="psr-kpi-rail"><EditorialKpiRail theme={theme} items={items} columns={items.length <= 5 ? items.length : undefined} /></div>;
}

/* ── Coverage disclosure ───────────────────────────────────────────────── */

function disclosureRows(states: PsrSourceCoverageState[], retainedSources: ReadonlySet<string>): Array<{ key: string; state: PsrCoverageState; names: string; text: string }> {
  const read = states.filter(row => retainedSources.has(row.sourceId) && (row.coverage === 'full' || row.coverage === 'partial'));
  const out = read.map(row => ({ key: `read-${row.sourceId}`, state: row.coverage, names: psrSourceLabel(row.sourceId),
    text: row.coverage === 'full' ? 'Completed.' : 'Partial read; remaining records were not fully retrieved.' }));
  const gaps = states.length - read.length;
  if (gaps) out.push({ key: 'unread-gap', state: 'unreported', names: `${gaps} unavailable or unread source attempt(s)`,
    text: 'Omitted from evidence. The collection receipt retains diagnostics; this gap does not establish the absence of concerns.' });
  return out;
}

/**
 * The coverage disclosure (SPEC §1 "Partial layer"): stance sources and
 * context sources, never mixed. Unread attempts are aggregate gaps,
 * never evidence rows or provider diagnostics. Shown whenever any source is not Full.
 */
export function CoverageDisclosure({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  if (!view.scan || !view.partial) return null;
  const roles: Array<{ role: PsrSourceCoverageState['role']; label: string }> = [
    { role: 'stance', label: 'Stance sources' },
    { role: 'context', label: 'Context sources' },
    { role: 'requested', label: 'Other coverage rows' },
  ];
  const coverageOnly = view.state === 'coverage-only';
  const anyFailed = view.coverage.some(row => row.coverage === 'failed');
  const label = coverageOnly ? 'No usable coverage' : anyFailed ? 'Coverage gap' : 'Partial coverage';
  return (
    <PsrCaveat theme={theme} label={label} icon={coverageOnly ? 'omit' : 'info'} testId="psr-coverage-disclosure">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {roles.map(({ role, label: groupLabel }) => {
          const states = view.coverage.filter(row => row.role === role);
          if (!states.length) return null;
          const tally = psrCoverageTally(view.coverage, role);
          return (
            <div key={role} data-testid={`psr-disclosure-${role}`} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <PsrGroupHeading theme={theme} label={groupLabel} detail={role === 'requested' ? `${tally.total} not attempted this scan` : psrCoverageTallyText(tally)} />
              {disclosureRows(states, retainedSentimentSourceIds({ publicSentimentMeta: view.v2 })).map(row => (
                <div key={row.key} style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                  <div style={{ flex: '0 0 118px' }}><CoverageMark theme={theme} state={row.state} /></div>
                  <p style={{ margin: 0, flex: '1 1 auto', minWidth: 0, fontFamily: PSR_SERIF, fontSize: 13, lineHeight: 1.55, color: roleColor(theme, 'body') }}>
                    <span style={{ fontWeight: 600, color: roleColor(theme, 'ink') }}>{row.names}.</span> {row.text}
                  </p>
                </div>
              ))}
            </div>
          );
        })}
        <p style={{ margin: 0, fontFamily: PSR_SERIF, fontSize: REPORT_TEXT_PX.label, fontStyle: 'italic', color: roleColor(theme, 'muted') }}>{PSR_DISCLOSURE_FOOTNOTE}</p>
      </div>
    </PsrCaveat>
  );
}

/** "Empty is not neutral" — always beside a status block. */
export function EmptyIsNotNeutralCallout({ theme }: { theme: ReportTheme }): React.ReactElement {
  return <PsrCaveat theme={theme} label="Empty is not neutral" testId="psr-empty-not-neutral">{PSR_EMPTY_IS_NOT_NEUTRAL}</PsrCaveat>;
}

/** Chapter 01 in one place: hero or status, sentence, strip, KPIs, disclosure. */
export function SentimentExecutiveBlocks({ theme, view, reason, entryCount }: { theme: ReportTheme; view: PsrReportView; reason?: string; entryCount?: number }): React.ReactElement {
  const assessed = view.index !== undefined;
  return (
    <div data-testid="psr-executive-blocks" data-psr-state={view.state} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {assessed ? (
        <>
          <SentimentHero theme={theme} view={view} />
          <SentimentHeroSentence theme={theme} view={view} />
          <LocalRecordUnitStrip theme={theme} view={view} />
        </>
      ) : (
        <SentimentStatusBlock theme={theme} view={view} reason={reason} />
      )}
      {(assessed || view.state !== 'assessed') && <SentimentKpiRail theme={theme} view={view} entryCount={entryCount} />}
      {view.state === 'no-dated-local-stance' && <EmptyIsNotNeutralCallout theme={theme} />}
      <CoverageDisclosure theme={theme} view={view} />
    </div>
  );
}
